import { useQueries } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { LanguageCode } from "../models/enums/LanguageCode";
import { FreelancerProfile } from "../models/FreelancerProfile";
import { entityTranslationsQuery } from "./useTranslations";

export const FREELANCER_PROFILE_ENTITY = "FreelancerProfile";
export const PROFESSIONAL_EXPERIENCE_ENTITY = "FreelancerProfileProfessionalExperience";

const NON_PRIMARY_LANGUAGES = Object.values(LanguageCode)
	.filter(language => language !== LanguageCode.Fr)
	.map(language => language.toLowerCase());

export const hasTextContent = (value: string | null | undefined): boolean =>
	!!value && value.trim() !== "" && value !== "<p></p>";

const toPropertyName = (field: string) => field.charAt(0).toUpperCase() + field.slice(1);

const cellKey = (entityType: string, entityId: number, propertyName: string) =>
	`${entityType}:${entityId}:${propertyName}`;

/**
 * Text authored in a non-primary language lives only in its translation cells: the FR columns
 * stay empty until the profile is Accepted. Checking the columns alone locks an EN-authored talent
 * out of every step after "My presentation" as soon as they reload.
 */
export const usePresentationStepContentInAnyLanguage = (
	freelancerProfile: FreelancerProfile | null | undefined
) => {
	const cellTargets = useMemo(() => {
		if (!freelancerProfile) return [];

		const entities: { entityType: string; entityId: number }[] = [];
		if (!hasTextContent(freelancerProfile.presentation)) {
			entities.push({ entityType: FREELANCER_PROFILE_ENTITY, entityId: freelancerProfile.id });
		}
		freelancerProfile.professionalExperiences.forEach(experience => {
			if (
				experience.id != null &&
				(!hasTextContent(experience.jobTitle) || !hasTextContent(experience.missionDescription))
			) {
				entities.push({ entityType: PROFESSIONAL_EXPERIENCE_ENTITY, entityId: experience.id });
			}
		});

		return entities.flatMap(entity =>
			NON_PRIMARY_LANGUAGES.map(languageCode => ({ ...entity, languageCode }))
		);
	}, [freelancerProfile]);

	const filledCellKeys = useQueries({
		queries: cellTargets.map(({ entityType, entityId, languageCode }) => ({
			...entityTranslationsQuery(entityType, entityId, languageCode),
			staleTime: 0
		})),
		combine: results =>
			results.flatMap((result, index) =>
				Object.entries(result.data ?? {})
					.filter(([, value]) => hasTextContent(value))
					.map(([propertyName]) =>
						cellKey(cellTargets[index].entityType, cellTargets[index].entityId, propertyName)
					)
			)
	});

	// Callers list the returned checker in effect deps that set state: its identity must only
	// change when the filled cells do, never on a mere re-render.
	const filledCellsSignature = filledCellKeys.join("|");
	const filledCells = useMemo(
		() => new Set(filledCellsSignature.split("|")),
		[filledCellsSignature]
	);

	return useCallback(
		(
			entityType: string,
			entityId: number | undefined,
			field: string,
			primaryValue: string | null | undefined
		): boolean =>
			hasTextContent(primaryValue) ||
			(entityId != null && filledCells.has(cellKey(entityType, entityId, toPropertyName(field)))),
		[filledCells]
	);
};
